import { PASSWORD_MIN_LENGTH, resetLoginPasswordSchema } from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Spinner,
  Table,
} from "@/components/ui";
import { apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";
import { NewUserPanel } from "./NewUserPanel";
import { generateTemporaryPassword } from "./temporary-password";

type Role = { id: string; key: string; name: string };
type Member = {
  id: string;
  email: string;
  fullName: string | null;
  status: "active" | "suspended" | "revoked";
  isMe: boolean;
  roles: Role[];
  /** conta de usuário desta empresa (login por usuário + código); null = entra por e-mail */
  username: string | null;
  contactEmail: string | null;
  mustChangePassword: boolean;
};

const STATUS: Record<Member["status"], { label: string; tone: "ok" | "warn" | "error" }> = {
  active: { label: "Ativo", tone: "ok" },
  suspended: { label: "Suspenso", tone: "warn" },
  revoked: { label: "Sem acesso", tone: "error" },
};

export function UsersPage() {
  const can = useCan();
  const manage = can("admin.usuarios.gerenciar");
  const createUsers = can("admin.usuarios.convidar");
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<Member | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetError, setResetError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);
  const queryClient = useQueryClient();
  const members = useQuery({
    queryKey: ["usuarios"],
    queryFn: () => apiFetch<Member[]>("/usuarios"),
  });
  const roles = useQuery({ queryKey: ["papeis"], queryFn: () => apiFetch<Role[]>("/papeis") });
  const [editing, setEditing] = useState<{ member: Member; roleIds: string[]; key: string } | null>(
    null,
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["usuarios"] });

  const saveRoles = useMutation({
    mutationFn: (e: { member: Member; roleIds: string[]; key: string }) =>
      apiFetch(`/usuarios/${e.member.id}/papeis`, {
        method: "PUT",
        body: { roleIds: e.roleIds },
        idempotencyKey: e.key,
      }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "suspender" | "reativar" | "revogar" }) =>
      apiFetch<{ loginSync: "ok" | "failed" | null }>(`/usuarios/${id}/${action}`, {
        method: "POST",
      }),
    onSuccess: async (r) => {
      setNotice(
        r.loginSync === "failed"
          ? {
              tone: "warn",
              text: "Situação alterada no CaféGestor, mas o serviço de login não respondeu. O acesso já segue a nova situação; repita a ação para sincronizar o login.",
            }
          : null,
      );
      await refresh();
    },
  });

  const reset = useMutation({
    mutationFn: ({ member, password }: { member: Member; password: string }) =>
      apiFetch(`/usuarios/${member.id}/redefinir-senha`, { method: "POST", body: { password } }),
    onSuccess: (_r, { member }) => {
      setResetting(null);
      setResetPassword("");
      setNotice({
        tone: "ok",
        text: `Senha de ${member.username ?? ""} redefinida. As sessões abertas foram encerradas e a troca será pedida no próximo acesso.`,
      });
      void refresh();
    },
  });

  function submitReset(e: FormEvent) {
    e.preventDefault();
    if (!resetting) return;
    const parsed = resetLoginPasswordSchema.safeParse({ password: resetPassword });
    if (!parsed.success) return setResetError(parsed.error.issues[0]?.message ?? "Senha inválida.");
    setResetError(null);
    reset.mutate({ member: resetting, password: parsed.data.password });
  }

  if (members.isPending) return <Spinner />;
  if (members.isError) return <Alert tone="error">{errorMessage(members.error)}</Alert>;

  return (
    <div className="space-y-6">
      <PageTitle
        title="Usuários"
        description="Pessoas com acesso a esta empresa e seus papéis."
        actions={
          createUsers && !creating ? (
            <Button onClick={() => setCreating(true)}>Novo usuário</Button>
          ) : undefined
        }
      />
      {changeStatus.isError && <Alert tone="error">{errorMessage(changeStatus.error)}</Alert>}
      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
      {creating && <NewUserPanel roles={roles.data ?? []} onClose={() => setCreating(false)} />}
      {resetting && (
        <Card
          title={`Redefinir a senha de ${resetting.fullName ?? resetting.username ?? ""}`}
          actions={
            <Button size="sm" variant="ghost" onClick={() => setResetting(null)}>
              Fechar
            </Button>
          }
        >
          <p className="mb-3 text-sm text-muted-foreground">
            A pessoa entra com esta senha temporária e cria uma nova no primeiro acesso. As sessões
            abertas (inclusive no tablet) são encerradas.
          </p>
          {(resetError || reset.isError) && (
            <Alert tone="error">{resetError ?? errorMessage(reset.error)}</Alert>
          )}
          <form onSubmit={submitReset} className="mt-3 flex flex-wrap items-end gap-2">
            <div className="min-w-64 flex-1">
              <Field label="Senha temporária" hint={`Mínimo de ${PASSWORD_MIN_LENGTH} caracteres.`}>
                <Input
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  autoComplete="new-password"
                  spellCheck={false}
                />
              </Field>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setResetPassword(generateTemporaryPassword())}
            >
              Gerar
            </Button>
            <Button type="submit" disabled={reset.isPending}>
              Redefinir senha
            </Button>
          </form>
        </Card>
      )}
      <Card>
        <Table head={["Pessoa", "Papéis", "Situação", ""]}>
          {members.data.map((m) => (
            <tr key={m.id}>
              <td className="px-3 py-2">
                <div className="font-medium">
                  {m.fullName ?? m.username ?? m.email} {m.isMe && <Badge>você</Badge>}{" "}
                  {m.mustChangePassword && <Badge tone="warn">troca de senha pendente</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {m.username
                    ? `Usuário: ${m.username}${m.contactEmail ? ` · ${m.contactEmail}` : ""}`
                    : m.email}
                </div>
              </td>
              <td className="px-3 py-2">{m.roles.map((r) => r.name).join(", ") || "—"}</td>
              <td className="px-3 py-2">
                <Badge tone={STATUS[m.status].tone}>{STATUS[m.status].label}</Badge>
              </td>
              <td className="space-x-2 whitespace-nowrap px-3 py-2 text-right">
                {manage && !m.isMe && (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        setEditing({
                          member: m,
                          roleIds: m.roles.map((r) => r.id),
                          key: newIdempotencyKey(),
                        })
                      }
                    >
                      Papéis
                    </Button>
                    {m.username && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setResetting(m);
                          setResetPassword("");
                          setResetError(null);
                        }}
                      >
                        Redefinir senha
                      </Button>
                    )}
                    {m.status === "active" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => changeStatus.mutate({ id: m.id, action: "suspender" })}
                      >
                        Suspender
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => changeStatus.mutate({ id: m.id, action: "reativar" })}
                      >
                        Reativar
                      </Button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </Card>

      {editing && (
        <Card
          title={`Papéis de ${editing.member.fullName ?? editing.member.email}`}
          actions={
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Fechar
            </Button>
          }
        >
          <div className="grid gap-2 sm:grid-cols-2">
            {roles.data?.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.roleIds.includes(r.id)}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      roleIds: e.target.checked
                        ? [...editing.roleIds, r.id]
                        : editing.roleIds.filter((id) => id !== r.id),
                    })
                  }
                />
                {r.name}
              </label>
            ))}
          </div>
          {saveRoles.isError && (
            <div className="mt-3">
              <Alert tone="error">{errorMessage(saveRoles.error)}</Alert>
            </div>
          )}
          <Button
            className="mt-4"
            disabled={saveRoles.isPending}
            onClick={() => saveRoles.mutate(editing)}
          >
            Salvar papéis
          </Button>
        </Card>
      )}
    </div>
  );
}
